import type { Point2D } from 'Common/types/generic/Points'
import type { IMarkerArea } from 'Common/utils/markerArea/markerArea'
import type { TransformMatrixType } from '../markerArea/types'

interface IPositionHandler {
  matrix: TransformMatrixType
  scale: number
  position: { x: number; y: number }
  _previousPanPoint: { x: number; y: number }
  isDirty: boolean
  imageElement: HTMLElement
  markerArea: IMarkerArea
}

class PositionHandler implements IPositionHandler {
  matrix: TransformMatrixType
  scale: number
  position: { x: number; y: number }
  _previousPanPoint: { x: number; y: number }
  isDirty: boolean
  imageElement: HTMLImageElement
  markerArea: IMarkerArea
  onTransform: (position: Point2D, transform: TransformMatrixType) => void

  constructor(imageElement: HTMLImageElement, markerArea: IMarkerArea) {
    //Matrix indices correspond with css matrix() function
    this.matrix = [1, 0, 0, 1, 0, 0]
    this.scale = 1
    this.isDirty = true
    this.imageElement = imageElement
    this.markerArea = markerArea
    this.imageElement.style.transformOrigin = 'top left'
    this.position = { x: 0, y: 0 }
    this.applyTransform()
  }

  /**
   * Applies css transform to the element from the defined matrix
   */
  applyTransform(): void {
    const m = this.matrix //alias for legibility and dx
    if (this.isDirty) this.updateTransformMatrix()
    this.imageElement.style.transform = `matrix(${m[0]},${m[1]},${m[2]},${m[3]},${m[4]},${m[5]})`
    this.markerArea.transformMatrix = m as TransformMatrixType
    if (typeof this.onTransform === 'function') this.onTransform(this.position, this.matrix as TransformMatrixType)
  }

  /**
   * Updates the matrix values based on the position and scaled values
   */
  updateTransformMatrix(): void {
    const m = this.matrix //alias for legibility and dx
    m[3] = m[0] = this.scale
    m[2] = m[1] = 0
    m[4] = this.position.x
    m[5] = this.position.y
    this.isDirty = false
  }

  /**
   * Calculates the x,y coordinates in relation to the position of the element
   * @param point (x,y) coordinates; expected to derive from event client x and client y
   */
  clientToLocalCoordinates(point: Point2D) {
    //Using parent element as it's bounding rectangle is uneffected by transform
    const boundingRect = this.imageElement.parentElement.getBoundingClientRect()
    //point.x - boundRect.left calcs the x position relative to the contentDiv
    //point.y - boundRect.top calcs the y position relative to the contentDiv
    return {
      x: point.x - boundingRect.left,
      y: point.y - boundingRect.top,
    }
  }

  /**
   * Updates the position property x and y values in order to apply a pan transform
   * @param amount (x,y) coordinates; expected to derive from event client x and client y and translated to local coordinates
   */
  calculatePan(amount: Point2D): void {
    //TODO Add bounds so user can not pan image out of view
    if (this.isDirty) this.updateTransformMatrix()
    this.position.x += amount.x
    this.position.y += amount.y
    this.isDirty = true
  }

  /**
   * Scales the position x and y values and translates them to the given point
   * @param at (x,y) coordinates; expected to derive from event client x and client y and translated to local coordinates
   * @param amount CSS scale value
   */
  calculateScale(zoomPoint: Point2D, amount: number): void {
    if (this.isDirty) {
      this.updateTransformMatrix()
    }

    // Only apply corrections if there is a change in scale
    if (amount !== this.scale) {
      const newScaleRatio = amount / this.scale

      // Calculate the position as if scaling from top-left (default origin)
      // This part finds out how far 'at' would move due to scaling
      // and adjusts the position to keep 'at' stationary
      this.position.x = (this.position.x - zoomPoint.x) * newScaleRatio + zoomPoint.x
      this.position.y = (this.position.y - zoomPoint.y) * newScaleRatio + zoomPoint.y
    }
    if (amount === 1) {
      this.position = {
        x: 0,
        y: 0,
      }
    }

    // Update scale and mark as dirty
    this.scale = amount
    this.isDirty = true
  }

  /**
   * Pans the element to the given point
   * @param point (x,y) coordinates; expected to derive from event client x and client y
   */
  pan(point: Point2D): void {
    const localPanPoint = this.clientToLocalCoordinates(point)
    const panAmount = {
      x: localPanPoint.x - this.previousPanPoint.x,
      y: localPanPoint.y - this.previousPanPoint.y,
    }
    this.calculatePan(panAmount)
    this.imageElement.style.removeProperty('transition') //remove smoothing for better panning responsiveness
    this.applyTransform()
    this.previousPanPoint = point
  }

  /**
   * Zooms the image to the given scale and point. Zooms into center of image if no point provided.
   * @param scale CSS scale value
   * @param point (x,y) coordinates; expected to derive from event client x and client y
   */
  zoom(scale: number, point?: Point2D): void {
    let localZoomPoint

    if (point) localZoomPoint = this.clientToLocalCoordinates(point)
    else
      localZoomPoint = {
        x: this.imageElement.clientWidth / 2,
        y: this.imageElement.clientHeight / 2,
      }

    this.calculateScale(localZoomPoint, scale)
    //this.imageElement.style.transition = 'transform 0.35s linear' //smooths zooming
    this.applyTransform()
  }

  public get previousPanPoint(): Point2D {
    return this._previousPanPoint
  }

  public set previousPanPoint(point: Point2D) {
    const localPanPoint = this.clientToLocalCoordinates(point)
    this._previousPanPoint = localPanPoint
  }
}

export { PositionHandler }
